import { DocumentRecordsListSortBy, DocumentRecordsSortDirection } from '@/__generated__/graphql';
import client from '@/apollo-client';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { OpenFileIcon } from '@/assets/static/icons/open-file';
import { UploadFileIcon } from '@/assets/static/icons/upload-file';
import { AiAssistant, aiPaneTabsOnEditFieldPages } from '@/components/aiAssistant';
import { GET_AI_ASSETS_QUERY, PROCESS_AI_ASSET_MUTATION } from '@/components/aiAssistant/queries';
import { IAiAssets, IAiRunPromptAnswerCitation } from '@/components/aiAssistant/types';
import { Accordion } from '@/components/basicComponents/accordion';
import Button from '@/components/basicComponents/button';
import FileUpload from '@/components/basicComponents/fileUpload';
import { ISelectValue } from '@/components/basicComponents/select';
import Tag from '@/components/basicComponents/tag';
import { Investment, IOfferingMaterialsAttachments } from '@/components/dealCatalog-page/types';
import { isImageExtension } from '@/components/dueDiligence-page/dueDiligenceRecord/utils';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { GET_ASSET_SIGNED_URL_QUERY } from '@/components/settings-page/queries';
import {
  STRATEGY_DETAILS_PAGE_CREATE_DOCUMENT_RECORD_MUTATION,
  STRATEGY_DETAILS_PAGE_DELETE_DOCUMENT_FIELD_ANSWER_ATTACHMENT_MUTATION,
  STRATEGY_DETAILS_PAGE_GET_DOCUMENT_RECORD_QUERY,
  STRATEGY_DETAILS_PAGE_LIST_DOCUMENT_RECORDS_QUERY,
  STRATEGY_DETAILS_PAGE_UPDATE_DOCUMENT_FIELD_ANSWER_MUTATION,
  STRATEGY_DETAILS_PAGE_UPDATE_INVESTMENT_OFFERING_MATERIALS_ATTACHMENT_MUTATION
} from '@/components/strategyDetails-page/tabs/updatesTab/queries';
import DropdownActions from '@/components/table/dropdownActions';
import { FieldEditorModalPage } from '@/features/fieldEditorModalPages/FieldEditorModalPage';
import { useResponsive } from '@/hooks/use-responsive';
import { useGoBack } from '@/hooks/useGoBack';
import { IFieldEditor } from '@/shared/types/fieldEditor';
import { convertDocumentRecordFieldToFieldEditor, FieldList } from '@/shared/ui/fieldList';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { extractAndFormatDate, extractAndFormatDollar, extractAndFormatNumber, extractAndFormatPercentage } from '@/shared/utils/fieldEditor';
import guid from '@/shared/utils/guid';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { gql, NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { AIFieldType } from '@trace-8/trace8-schema';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { DealDocumentsModalPage } from './dealDocumentsModal-page';
import DeleteAttachment from './modals/deleteAttachment';

const GET_INVESTMENT_ATTACHMENTS_QUERY = gql(`
  query EditStrategyDescriptionPageInvestment($id: String!) {
    Investment(id: $id) {
      id
      offeringMaterialsAttachments {
        id
        asset {
          url
          assetKey
        }
        visibility
      }
    }
  }
`);

const visibilityOptions: ISelectValue[] = [
  { label: 'Not Visible to Advisors', value: 'Not Visible to Advisors' },
  { label: 'Visible Pre-Commitment', value: 'Visible Pre-Commitment' },
  { label: 'Visible Post-Commitment', value: 'Visible Post-Commitment' }
];

const getDataForUpdate = (row: IFieldEditor): { value: string; commentary: string } => {
  switch (row.documentFieldType) {
    case getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT):
      return {
        value: row.AI.answers[0],
        commentary: null
      };
    case getEnumKeyByValue(AIFieldType, AIFieldType.SHORT_TEXT):
      return {
        value: row.AI.answers[0],
        commentary: null
      };
    case getEnumKeyByValue(AIFieldType, AIFieldType.NUMBER):
      return {
        value: extractAndFormatNumber(row.AI.answers[0]),
        commentary: row.AI.answers[0]
      };
    case getEnumKeyByValue(AIFieldType, AIFieldType.DOLLAR):
      return {
        value: extractAndFormatDollar(row.AI.answers[0]),
        commentary: row.AI.answers[0]
      };
    case getEnumKeyByValue(AIFieldType, AIFieldType.PERCENTAGE):
      return {
        value: extractAndFormatPercentage(row.AI.answers[0]),
        commentary: row.AI.answers[0]
      };
    case getEnumKeyByValue(AIFieldType, AIFieldType.DATE):
      return {
        value: extractAndFormatDate(row.AI.answers[0]),
        commentary: row.AI.answers[0]
      };
    default:
      return {
        value: '',
        commentary: ''
      };
  }
};

const EditStrategyDescription = () => {
  const { isMobile, isTablet } = useResponsive();
  const goBack = useGoBack();
  const params = useParams();

  const [searchParams, setSearchParams] = useSearchParams();

  const backToTitle = searchParams.get('backToTitle') || '';

  const [uploadedFiles, setUploadedFiles] = useState<IOfferingMaterialsAttachments[] | []>([]);
  const [documentRecordId, setDocumentRecordId] = useState<string | null>(null);
  const [documentRecordLoading, setDocumentRecordLoading] = useState(true);
  const [isAiAssistantOpen, setIsAiAssistantOpen] = useState(false);
  const [generateAnswersLoading, setGenerateAnswersLoading] = useState(false);
  const [isGenerateAnswersLoadingCompleted, setIsGenerateAnswersLoadingCompleted] = useState(false);
  const [allFields, setAllFields] = useState<IFieldEditor[] | null>(null);
  const [currentField, setCurrentField] = useState<IFieldEditor | null>(null);
  const [editFieldPageType, setEditFieldPageType] = useState('default');
  const [scrollTo, setScrollTo] = useState(0);
  const [currentAttachment, setCurrentAttachment] = useState<IOfferingMaterialsAttachments | null>(null);
  const [isDealDocumentsModalPageOpen, setIsDealDocumentsModalPageOpen] = useState(false);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [filesForAiAssistant, setFilesForAiAssistant] = useState<IAiAssets[]>([]);
  const [aiRunStatusRefetch, setAiRunStatusRefetch] = useState<{ getAIRunStatusRefetch: () => void }>({ getAIRunStatusRefetch: () => {} });
  const [citationsTabData, setCitationsTabData] = useState<
    {
      id: string;
      fieldName: string;
      citations: IAiRunPromptAnswerCitation[];
    }[]
  >([]);

  const handleAiRunStatusRefetch = (getAIRunStatusRefetch: () => void) => {
    setAiRunStatusRefetch({ getAIRunStatusRefetch });
  };

  const triggerAiRunStatusRefetch = () => {
    aiRunStatusRefetch.getAIRunStatusRefetch();
  };

  const [createDocumentRecord] = useMutation(STRATEGY_DETAILS_PAGE_CREATE_DOCUMENT_RECORD_MUTATION);
  const [updateAttachment] = useMutation(STRATEGY_DETAILS_PAGE_UPDATE_INVESTMENT_OFFERING_MATERIALS_ATTACHMENT_MUTATION);
  const [updateDocumentFieldAnswer] = useMutation(STRATEGY_DETAILS_PAGE_UPDATE_DOCUMENT_FIELD_ANSWER_MUTATION);
  const [deleteAttachment] = useMutation(STRATEGY_DETAILS_PAGE_DELETE_DOCUMENT_FIELD_ANSWER_ATTACHMENT_MUTATION);
  const [processAIAsset] = useMutation(PROCESS_AI_ASSET_MUTATION);

  useEffect(() => {
    if (editFieldPageType === 'default' && !isDealDocumentsModalPageOpen && scrollTo) {
      window.scrollTo(0, scrollTo);
      setScrollTo(0);
    }
  }, [editFieldPageType, isDealDocumentsModalPageOpen]);

  useEffect(() => {
    setCitationsTabData([{ id: currentField?.id, fieldName: currentField?.name, citations: currentField?.AI?.citations ?? [] }]);
  }, [currentField, editFieldPageType]);

  const { networkStatus: investmentOfferingMaterialsNetworkStatus, refetch: investmentOfferingMaterialsRefetch } = useQuery<{
    Investment: Investment;
  }>(GET_INVESTMENT_ATTACHMENTS_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !params?.strategyId,
    variables: { id: params?.strategyId },
    onCompleted: (data) => {
      const filteredAttachments =
        data?.Investment?.offeringMaterialsAttachments?.filter((attachment) => !attachment?.asset?.assetKey?.includes('/mu/')) || [];

      setUploadedFiles(filteredAttachments);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { loading: listDocumentRecordsLoading } = useQuery(STRATEGY_DETAILS_PAGE_LIST_DOCUMENT_RECORDS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    skip: !params?.strategyId,
    variables: {
      data: {
        investmentId: params?.strategyId,
        sortBy: DocumentRecordsListSortBy.Name,
        sortDirection: DocumentRecordsSortDirection.Asc,
        documentTypeFilter: ['sd'],
        limit: 1
      },
      fieldNames: []
    },
    onCompleted: (data) => {
      if (data.listDocumentRecords.records.length) {
        setDocumentRecordId(data.listDocumentRecords.records[0].id);
        return;
      }
      createDocumentRecord({
        variables: {
          data: {
            documentType: 'sd',
            investmentId: params?.strategyId
          }
        },
        onCompleted: (data) => {
          setDocumentRecordId(data.createDocumentRecord.id);
        }
      });
    },
    onError: (error) => {
      console.error(error);
      setDocumentRecordLoading(false);
    }
  });

  const { refetch: documentRecordRefetch } = useQuery(STRATEGY_DETAILS_PAGE_GET_DOCUMENT_RECORD_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    skip: !documentRecordId,
    variables: {
      id: documentRecordId
    },
    onCompleted: (data) => {
      const { fields } = data.getDocumentRecord;

      const allEditFields: IFieldEditor[] = convertDocumentRecordFieldToFieldEditor(fields);

      setAllFields(allEditFields);
      setDocumentRecordLoading(false);
    },
    onError: (error) => {
      console.error(error);
      setDocumentRecordLoading(false);
    }
  });

  const handleGenerateAnswersLoadingChange = (loadingStatus: boolean, showCompleteIcon: boolean) => {
    setGenerateAnswersLoading(loadingStatus);
    setIsGenerateAnswersLoadingCompleted(showCompleteIcon);
  };

  const handleFilesForAiAssistant = (files: IAiAssets[]) => {
    setFilesForAiAssistant(files);
  };

  const openAdvancedOptions = () => {
    setIsAiAssistantOpen(false);
    setEditFieldPageType('advanced-options');
  };

  const onEditField = (fieldItem: IFieldEditor) => {
    setScrollTo(window.scrollY);
    setEditFieldPageType(fieldItem.documentFieldType);
    const editField = allFields?.find((field) => field.id === fieldItem.id);
    setCurrentField(editField || null);
    documentRecordRefetch();
  };

  const closeEditPage = () => {
    setEditFieldPageType('default');
    setCurrentField(null);
    documentRecordRefetch();
  };

  const openAttachment = async (assetKey: string) => {
    const { data } = await client.query({
      fetchPolicy: 'network-only',
      query: GET_ASSET_SIGNED_URL_QUERY,
      variables: {
        assetKey: assetKey
      }
    });
    window.open(`${data.getAssetSignedUrl}`, '_blank');
  };

  const openDealDocumentsPage = (attachment?: IOfferingMaterialsAttachments) => {
    setScrollTo(window.scrollY);
    setIsDealDocumentsModalPageOpen(true);
    if (attachment) {
      setCurrentAttachment(attachment);
    }
  };

  const openModalWindow = (type: string, attachment: IOfferingMaterialsAttachments) => {
    setModalWindow({ type, isOpen: true });
    if (attachment) {
      setCurrentAttachment(attachment);
    }
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
    setCurrentAttachment(null);
  };

  const addNewAttachments = async (file: File, visibility: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('visibility', visibility);

    const { data } = await axios.post(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/investment-offering-materials-attachment/investment/${params?.strategyId}/document-record/${documentRecordId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );

    if (file.name.toLowerCase().endsWith('.pdf')) {
      await processAIAsset({
        variables: {
          data: {
            investmentId: params?.strategyId,
            recordId: documentRecordId,
            filename: data.fileName,
            type: 'sd',
            assetKey: data.assetKey
          }
        }
      });
    }

    if (file.name.toLowerCase().endsWith('.pdf')) {
      const { data: aiAssetsData } = await client.query<{ getDealAssistentAIAssets: IAiAssets[] }>({
        query: GET_AI_ASSETS_QUERY,
        variables: {
          data: {
            recordId: documentRecordId
          }
        }
      });
      setFilesForAiAssistant(aiAssetsData.getDealAssistentAIAssets);
    }

    await investmentOfferingMaterialsRefetch();
  };

  const updateAttachmentVisibility = async (visibility: string) => {
    if (currentAttachment.visibility !== visibility) {
      await updateAttachment({
        variables: {
          data: {
            id: currentAttachment.id,
            visibility: visibility
          }
        },
        onCompleted: () => {
          setUploadedFiles((prev) =>
            prev.map((file) => {
              if (file.id === currentAttachment.id) {
                return { ...file, visibility };
              }
              return file;
            })
          );
        }
      });
    }
  };

  const onFileUploadComplete = async () => {
    await investmentOfferingMaterialsRefetch();
  };

  const handleUpdateFieldAnswer = async ({
    id,
    recordId,
    value,
    commentary
  }: {
    id: string;
    recordId: string;
    value: string;
    commentary?: string;
  }) => {
    await updateDocumentFieldAnswer({
      variables: {
        data: {
          fieldId: id,
          recordId: recordId,
          value,
          commentary
        }
      }
    });
  };

  const handleDeleteAttachment = async ({ id }: { id: string }) => {
    await deleteAttachment({
      variables: {
        id
      }
    });
  };

  const handleUploadAttachment = async ({
    file,
    recordId,
    fieldId,
    fieldAnswerId
  }: {
    file: File;
    recordId: string;
    fieldId: string;
    fieldAnswerId: string;
  }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('includeInAutomatedContent', 'true');

    const { data: attachmentData } = await axios.post(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/document-field-answer/document-record/${recordId}/document-field-answer/${fieldId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );

    const { assetUrl, assetKey } = attachmentData || {};

    return { id: guid(), url: assetUrl, assetKey: assetKey || '' };
  };

  const onAcceptAiAnswer = async (field: IFieldEditor) => {
    const { value, commentary } = getDataForUpdate(field);

    await updateDocumentFieldAnswer({
      variables: {
        data: {
          fieldId: field.fieldId,
          recordId: field.recordId,
          value,
          commentary
        }
      }
    });

    await documentRecordRefetch();
  };

  const onClearField = async (field: IFieldEditor) => {
    const commentary =
      field?.AI?.answers?.length &&
      field.documentFieldType !== getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT) &&
      field.documentFieldType !== getEnumKeyByValue(AIFieldType, AIFieldType.SHORT_TEXT)
        ? ''
        : null;

    await updateDocumentFieldAnswer({
      variables: {
        data: {
          fieldId: field.fieldId,
          recordId: field.recordId,
          value: field?.AI?.answers?.length ? '' : null,
          commentary
        }
      }
    });

    await documentRecordRefetch();
  };

  const isLoading =
    investmentOfferingMaterialsNetworkStatus === NetworkStatus.loading ||
    investmentOfferingMaterialsNetworkStatus === NetworkStatus.setVariables ||
    listDocumentRecordsLoading ||
    documentRecordLoading;

  if (isLoading) {
    return <LoaderOnWholeScreen />;
  }

  if (isDealDocumentsModalPageOpen) {
    return (
      <DealDocumentsModalPage
        currentAttachment={
          currentAttachment
            ? {
                assetKey: currentAttachment?.asset?.assetKey,
                url: currentAttachment?.asset?.url,
                visibility: currentAttachment?.visibility
              }
            : null
        }
        visibilityOptions={visibilityOptions}
        addNewAttachments={addNewAttachments}
        updateAttachmentVisibility={updateAttachmentVisibility}
        handleClosePage={() => {
          setIsDealDocumentsModalPageOpen(false);
          setCurrentAttachment(null);
        }}
      />
    );
  }

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'delete-attachment' && (
        <DeleteAttachment
          isOpen={modalWindow.isOpen}
          currentAttachment={currentAttachment}
          recordId={documentRecordId}
          notVisibleTitle="Not Visible to Advisors"
          triggerAiRunStatusRefetch={triggerAiRunStatusRefetch}
          documentRecordRefetch={documentRecordRefetch}
          setUploadedFiles={setUploadedFiles}
          setFilesForAiAssistant={setFilesForAiAssistant}
          onClose={closeModalWindow}
        />
      )}
      <EditStrategyDescriptionWrapper>
        {((!isMobile && !isTablet) || !isAiAssistantOpen) && editFieldPageType !== 'default' && editFieldPageType !== 'advanced-options' && (
          <FieldEditorModalPage
            allFields={allFields}
            currentField={currentField}
            investmentId={params?.strategyId}
            editFieldPageType={editFieldPageType as keyof typeof AIFieldType}
            shortTextAdditionalProperties={{ maxLength: 140 }}
            setIsAiPaneOpen={setIsAiAssistantOpen}
            setCurrentField={setCurrentField}
            setEditFieldPageType={setEditFieldPageType}
            mainQueryRefetch={documentRecordRefetch}
            handleUpdateFieldAnswer={handleUpdateFieldAnswer}
            handleUploadAttachment={handleUploadAttachment}
            handleDeleteAttachment={handleDeleteAttachment}
            handleClose={closeEditPage}
          />
        )}
        {((!isMobile && !isTablet) || !isAiAssistantOpen) && editFieldPageType === 'default' && (
          <MainWrap>
            <Header
              aiButtonConfig={{
                isLoading: generateAnswersLoading,
                secondaryIcon: isGenerateAnswersLoadingCompleted && <ApprovedIcon width={20} height={20} fill={themeVar('context.light')} />,
                onClick: () => setIsAiAssistantOpen(!isAiAssistantOpen)
              }}
              modalControl={<GoBackButton handleClose={() => goBack({ fallBack: '/dealPipeline' })} backToTitle={backToTitle} />}
            />
            <PageTitle title="Edit Strategy Description" />
            <CustomPaddingWrap>
              <SectionsWrap>
                <Accordion title="Description">
                  <FieldList
                    fields={allFields}
                    mode="edit"
                    onEditField={onEditField}
                    onAcceptAiAnswer={onAcceptAiAnswer}
                    onClearField={onClearField}
                  />
                </Accordion>
                <Accordion title="Attachments">
                  {uploadedFiles.length ? (
                    <>
                      <PreviewWrapper>
                        {uploadedFiles.map((item: IOfferingMaterialsAttachments) => (
                          <FileUpload
                            key={item.asset.assetKey}
                            type={isImageExtension(item.asset.assetKey) ? 'image' : 'document'}
                            imageSrc={item.asset.url}
                            fileName={item.asset.assetKey.split('/').pop()}
                            withHover
                            additionalContent={
                              <Tag
                                title={item.visibility}
                                styleType={item.visibility !== 'Not Visible to Advisors' ? 'valid' : 'neutral'}
                                size="md"
                              />
                            }
                            customActionButtons={
                              <>
                                <OpenAttachmentWrapper id="infoWrap" onClick={() => openAttachment(item.asset.assetKey)}>
                                  <OpenFileIcon fill={themeVar('font.action')} />
                                </OpenAttachmentWrapper>
                                <DropdownActions
                                  items={[
                                    { name: 'Edit', onClick: () => openDealDocumentsPage(item) },
                                    { name: 'Delete', onClick: () => openModalWindow('delete-attachment', item) }
                                  ]}
                                />
                              </>
                            }
                          />
                        ))}
                      </PreviewWrapper>
                      <Button
                        onClick={() => openDealDocumentsPage()}
                        styleType="outline"
                        size="md"
                        width="max-content"
                        icon={<UploadFileIcon width={18} height={23} fill={themeVar('font.action')} />}
                      >
                        Upload
                      </Button>
                    </>
                  ) : (
                    <AttachmentsSectionZeroStateWrapper>
                      <AttachmentsSectionZeroStateText>No files have been added yet.</AttachmentsSectionZeroStateText>
                      <Button
                        onClick={() => openDealDocumentsPage()}
                        styleType="outline"
                        size="md"
                        width="max-content"
                        icon={<UploadFileIcon width={18} height={23} fill={themeVar('font.action')} />}
                      >
                        Upload
                      </Button>
                    </AttachmentsSectionZeroStateWrapper>
                  )}
                </Accordion>
              </SectionsWrap>
            </CustomPaddingWrap>
          </MainWrap>
        )}
        <AiAssistant
          isAiAssistantOpen={isAiAssistantOpen}
          ids={{
            recordId: documentRecordId,
            investmentId: params?.strategyId
          }}
          type="sd"
          filesForAiAssistant={filesForAiAssistant}
          tabs={editFieldPageType !== 'default' && editFieldPageType !== 'advanced-options' ? aiPaneTabsOnEditFieldPages : []}
          citationsTabData={citationsTabData}
          onFileUploadComplete={onFileUploadComplete}
          handleFilesForAiAssistant={handleFilesForAiAssistant}
          handleAiRunStatusRefetch={handleAiRunStatusRefetch}
          handleGenerateAnswersLoadingChange={handleGenerateAnswersLoadingChange}
          mainQueryRefetch={documentRecordRefetch}
          openAdvancedOptions={openAdvancedOptions}
          onClose={() => setIsAiAssistantOpen(false)}
        />
      </EditStrategyDescriptionWrapper>
    </>
  );
};

export default EditStrategyDescription;

const EditStrategyDescriptionWrapper = styled.div`
  display: flex;
  justify-content: center;
  transition: all 0.3s;
`;

const CustomPaddingWrap = styled(PaddingWrap)`
  margin: 20px 0;
`;

const SectionsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PreviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const OpenAttachmentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  height: 40px;
  width: 40px;
  opacity: 0;
  border-radius: 4px;
  border: 1px solid ${themeVar('font.action')};
  background: ${themeVar('layer.base')};
`;

const AttachmentsSectionZeroStateWrapper = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: column;
`;

const AttachmentsSectionZeroStateText = styled.div`
  padding: 10px 0;
  font-size: 19px;
  font-weight: 400;
  line-height: 26.6px;
  color: ${themeVar('font.weak')};
`;
